import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe2, Loader2, Send } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

export default function InvestorDirectory() {
  const [profiles, setProfiles] = useState(null);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/directory").then(({ data }) => setProfiles(data)).catch((e) => { console.error("directory load failed", e); setProfiles([]); });
  }, []);

  const request = async () => {
    setSending(true);
    try {
      await api.post(`/directory/${target.id}/intro-request`, { message: message.trim() });
      toast.success(`Intro request sent to ${target.name}`);
      setTarget(null); setMessage("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send request"); }
    finally { setSending(false); }
  };

  const q = query.trim().toLowerCase();
  const filtered = (profiles || []).filter((p) =>
    !q || [p.name, p.firm, p.bio, ...(p.expertise || [])].filter(Boolean).some((t) => t.toLowerCase().includes(q)));

  return (
    <AppShell title="Investor Directory" subtitle="Opted-in investors across the tenx network">
      {profiles !== null && profiles.length > 0 && (
        <div className="mb-4 max-w-sm">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, firm or expertise…" data-testid="directory-search" />
        </div>
      )}
      {profiles === null ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : profiles.length === 0 ? (
        <Card className="rounded-xl border border-dashed p-10 text-center">
          <Globe2 className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="mt-3 text-sm font-semibold text-foreground">No investors listed yet</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">Investors on tenx can opt into the directory from their portfolio page. Check back soon.</p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No investors match "{query}".</Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <Card key={p.id} className="flex flex-col rounded-xl border bg-card p-5" data-testid={`directory-card-${p.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-primary">{p.name?.charAt(0)?.toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-foreground">{p.name}</div>
                  {p.firm && <div className="truncate text-xs text-muted-foreground">{p.firm}</div>}
                </div>
              </div>
              {p.bio && <p className="mt-3 line-clamp-3 text-xs text-muted-foreground">{p.bio}</p>}
              {(p.expertise || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {p.expertise.map((t) => <Badge key={t} variant="secondary" className="font-normal">{t}</Badge>)}
                </div>
              )}
              <Button size="sm" variant="outline" className="mt-4 gap-1.5 self-start" onClick={() => setTarget(p)} data-testid={`intro-request-btn-${p.id}`}>
                <Send className="h-3.5 w-3.5" /> Request intro
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request an intro to {target?.name}</DialogTitle>
            <DialogDescription>Sent privately with a notification and email.</DialogDescription>
          </DialogHeader>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
            placeholder="Who are you, what are you building, and why this investor?" data-testid="intro-message" />
          <Button onClick={request} disabled={sending || !message.trim()} className="w-full gap-2" data-testid="intro-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request
          </Button>
          <p className="text-xs text-muted-foreground">The investor gets a notification and an email. Their contact details stay private until they reply.</p>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
